import { makeStyles, TextField, CircularProgress  } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { fetchCandidateRole } from '../../../api/candidateRole';
import { COLORS } from '../../../constants/colors';
import { useDebounce, useNotification } from '../../../hooks';

const useStyles = makeStyles({
  root: {
    width: '100%',
    marginBottom: '0.625rem',
  },
  container: {
    display: 'flex',
    backgroundColor: COLORS.grayScale[5],
    overflow: 'hidden',
    borderRadius: '0.3125rem',
    '& input, & textarea': {
      width: '100%',
      fontFamily: 'Raleway',
      fontSize: '1rem',
      lineHeight: '1.1875rem',
      boxSizing: 'border-box',
      border: 'none',
      outline: 'unset',
      color: COLORS.grayScale[1],
      '&::placeholder': {
        color: COLORS.grayScale[4],
      },
    },
    '& > :first-child': {
      marginLeft: '1.25rem',
    },
    '& input': {
      height: '3.125rem',
      padding: '1.0625rem 1.25rem',
      paddingLeft: 0,
    },
    '& textarea': {
      height: '100%',
      margin: '1.0625rem 1.25rem',
      resize: (props) => props.resize,
    },
  },
  invalid: {
    '& *': {
      color: `${COLORS.error} !important`,
    },
    '& > :first-child': {
      border: `1px solid ${COLORS.error}`,
    },
  },
  message: {
    margin: 0,
    marginTop: '0.25rem',
  },
  startIcon: {
    color: COLORS.grayScale[0],
    fontSize: '1.25rem',
    lineHeight: '1.25rem',
    margin: '0.9375rem 1.13rem 0.9375rem 0',
  }
});

const Searcher = ({ 
  className, 
  invalid, 
  startIcon,
  message,
  value: initialValue, 
  onSelect,
  ...props 
}) => {
  const classes = useStyles();
  const notifications = useNotification();

  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [value, setValue] = useState(initialValue);
  const [inputValue, setInputValue] = useState('');

  if (initialValue?.hash) onSelect(initialValue?.hash);

  const loading = open && results.length === 0;

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    fetchCandidateRoleDebounced();

    return () => {
      active = false;
    };
  }, [loading]);

  useEffect(() => {
    if (!open) {
      setResults([]);
    }
  }, [open]);

  const fetchCandidateRoleDebounced = useDebounce(
    (name) => {
      return fetchCandidateRole(name)
      .ready()
      .then((res) => {
        if (res.success) {
          setResults(res.data);
          if (!value?.hash && value?.name) {
            setValue(res.data.find(item => item.name === value.name));
          }
        } else
          notifications.error({
            message:
              'An error has occured while searching skills. Please, try again later',
          });
      });
    },

    1000
  );

  return (
    <Autocomplete
      value={value}
      onChange={(event, newValue) => {
        setValue(newValue);
        //onSelect(newValue?.hash);
        //console.log("autocomplete onchange",newValue)
        onSelect(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        //console.log("autocomplete setInputValue",newInputValue)
      }}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      getOptionSelected={(option, value) => option.name === value.name}
      getOptionLabel={(option) => option.name}
      options={results}
      loading={loading}
      renderInput={(params) => (
        <div
          className={
            invalid ? [classes.root, classes.invalid].join(' ') : classes.root
          }
        >
          <div
            className={
              className
                ? [classes.container, className].join(' ')
                : classes.container
            }
          >
            {startIcon && <div className={classes.startIcon}>{startIcon}</div>}
            <TextField
              {...params}
              {...props}
              placeholder="Search by Position"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          </div>
          {invalid && message && <p className={classes.message}>{message}</p>}
        </div>
      )}
    />
  );
};

Searcher.propTypes = {
  onSelect: PropTypes.func.isRequired,
};

export default Searcher;
